import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  gorevSapmaRaporu,
  gorevsizMesaiRaporu,
  type GorevSapmaSatiri,
  type GorevsizMesaiSatiri,
} from '../api/gorev';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { GorevDurumRozeti } from '../bilesenler/GorevParcalari';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { HataKutusu, Metin } from '../bilesenler/temel';
import { bugun, gunEkle, tarihMetni } from '../bicim/tarih';
import { KalibrasyonBolumu } from './GorevTipleri';

/**
 * GÖREV RAPORLARI.
 *
 * `gorev_arasi` İLE `gorevsiz_mesai` AYRI GÖSTERİLİR. `gorev_arasi` NORMAL
 * OPERASYONDUR (intikal, malzeme bekleme, araç teslimi — 4857/66 çalışma
 * süresi); yalnız `gorevsiz_mesai` göstergeye girer. Aynı kovada toplansaydı
 * müdür normal intikali "boş geçen zaman" sanır ve ekibi haksız yere
 * sıkıştırırdı.
 *
 * ORANLAR ONDALIK METİN. `sapmaOrani` ve `gorevsizOran` kayan noktaya
 * çevrilmez; olduğu gibi basılır.
 */

const VARSAYILAN_GERI_GUN = 30;

export function GorevRaporlari() {
  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [baslangic, baslangicYaz] = useState(() => gunEkle(bugun(), -VARSAYILAN_GERI_GUN));
  const [bitis, bitisYaz] = useState(() => bugun());

  // Her iki uç da `birimId` ZORUNLU istiyor (`AralikSorguDto` `@Matches`).
  const hazir = birimId !== undefined;

  const sapma = useQuery({
    queryKey: ['gorev', 'rapor-sapma', { birimId, baslangic, bitis }],
    queryFn: () => gorevSapmaRaporu(birimId!, baslangic, bitis),
    enabled: hazir,
  });

  const gorevsiz = useQuery({
    queryKey: ['gorev', 'rapor-gorevsiz', { birimId, baslangic, bitis }],
    queryFn: () => gorevsizMesaiRaporu(birimId!, baslangic, bitis),
    enabled: hazir,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <BirimSecici deger={birimId} degisti={birimIdYaz} kendiBirimiyleBasla />
        </div>
        <Metin etiket="Başlangıç" tur="date" deger={baslangic} degisti={baslangicYaz} />
        <Metin etiket="Bitiş" tur="date" deger={bitis} degisti={bitisYaz} />
      </div>

      {/* Birimden BAĞIMSIZ: birim seçilmeden de gösterilir (uç `birimId`
          istemiyor, isteğe bağlı süzgeç). */}
      <KalibrasyonBolumu birimId={birimId} />

      {!hazir ? (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Raporu görmek için bir birim seçin.
        </p>
      ) : (
        <>
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-slate-800">Görev sapma raporu</h2>
            <p className="text-xs text-slate-500">
              Beklenen işgücü ile fiili ölçüm karşılaştırılır. Ölçüm çalışma biçimine göre SEÇİLİR —
              birlikte: işgücü, dönüşümlü: takvim süresi.
            </p>
            <HataKutusu hata={sapma.error} />
            <SapmaTablosu satirlar={sapma.data ?? []} bekliyor={sapma.isPending} />
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-slate-800">Görevsiz mesai oranı</h2>
            <p className="text-xs text-slate-500">
              Göstergeye YALNIZ görevsiz mesai girer. Görev arası normal operasyondur (4857/66) ama
              ayrı sütunda gösterilir — “görevsiz düşük ama görev arası çok yüksek” de bir
              sinyaldir.
            </p>
            <HataKutusu hata={gorevsiz.error} />
            <GorevsizTablosu satirlar={gorevsiz.data ?? []} bekliyor={gorevsiz.isPending} />
          </section>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

const SAPMA_SUTUNLARI: Sutun<GorevSapmaSatiri>[] = [
  {
    baslik: 'Görev',
    hucre: (s) => (
      <span>
        <span className="block font-medium text-slate-900">{s.gorevTipiAd}</span>
        <span className="block text-xs text-slate-500">
          {tarihMetni(s.tarih)}
          {s.lokasyonMetni !== null && ` · ${s.lokasyonMetni}`}
        </span>
      </span>
    ),
  },
  { baslik: 'Durum', hucre: (s) => <GorevDurumRozeti durum={s.durum} />, daralt: true },
  {
    baslik: 'Beklenen',
    hucre: (s) => (s.beklenenIsgucuDk === null ? '—' : `${s.beklenenIsgucuDk} dk`),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Fiili',
    hucre: (s) => (
      <span className="whitespace-nowrap text-xs">
        <span className="block">{s.fiiliOlcumDk === null ? '—' : `${s.fiiliOlcumDk} dk`}</span>
        {s.kisiSayisi !== null && <span className="block text-slate-500">{s.kisiSayisi} kişi</span>}
      </span>
    ),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Sapma',
    hucre: (s) => <SapmaHucresi satir={s} />,
    daralt: true,
    hizala: 'sag',
  },
];

/**
 * SAPMA ORANI VE EKSİK PERSONEL BİRLİKTE.
 *
 * `eksikPersonelMi` AYRI TAŞINIR: süre uzaması eksik personelden geliyorsa
 * sapma "yavaş çalıştılar" diye okunmamalı. Gösterilmezse müdür yanlış
 * sonuç çıkarır.
 *
 * `sapmaOrani` ONDALIK METİN — olduğu gibi basılır. `1.150` beklenenin
 * %15 üstü demektir.
 */
function SapmaHucresi({ satir }: { satir: GorevSapmaSatiri }) {
  if (satir.sapmaOrani === null) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  // Yalnız RENK için sayısal karşılaştırma; basılan değer metnin kendisi.
  const oran = Number(satir.sapmaOrani);
  const renk =
    Number.isNaN(oran) || (oran >= 0.9 && oran <= 1.1)
      ? 'text-slate-700'
      : oran > 1.1
        ? 'text-amber-800'
        : 'text-sky-700';

  return (
    <span className="whitespace-nowrap text-xs">
      <span className={`block font-medium ${renk}`}>×{satir.sapmaOrani}</span>
      {satir.eksikPersonelMi === true && (
        <span className="block text-slate-500">eksik personel</span>
      )}
    </span>
  );
}

function SapmaTablosu({ satirlar, bekliyor }: { satirlar: GorevSapmaSatiri[]; bekliyor: boolean }) {
  return (
    <Tablo
      sutunlar={SAPMA_SUTUNLARI}
      satirlar={satirlar}
      anahtar={(s) => s.gorevId}
      bekliyor={bekliyor}
      bosMetin="Bu aralıkta gerçekleşmiş görev yok."
    />
  );
}

/* ------------------------------------------------------------------ */

const GOREVSIZ_SUTUNLARI: Sutun<GorevsizMesaiSatiri>[] = [
  {
    baslik: 'Birim',
    hucre: (g) => (
      <span>
        <span className="block font-medium text-slate-900">{g.birimAd}</span>
        <span className="block text-xs text-slate-500">
          {g.birimKod} · {tarihMetni(g.ay)}
        </span>
      </span>
    ),
  },
  {
    baslik: 'Görev',
    hucre: (g) => (g.gorevDk === null ? '—' : `${g.gorevDk} dk`),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Görev arası',
    // NORMAL OPERASYON — göstergeye girmez, ayrı sütunda durur.
    hucre: (g) => (
      <span className="whitespace-nowrap text-xs text-slate-600">
        {g.gorevArasiDk === null ? '—' : `${g.gorevArasiDk} dk`}
      </span>
    ),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Görevsiz',
    hucre: (g) => (
      <span className="whitespace-nowrap font-medium text-slate-900">
        {g.gorevsizDk === null ? '—' : `${g.gorevsizDk} dk`}
      </span>
    ),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Oran',
    hucre: (g) => <OranHucresi satir={g} />,
    daralt: true,
    hizala: 'sag',
  },
];

function OranHucresi({ satir }: { satir: GorevsizMesaiSatiri }) {
  if (satir.gorevsizOran === null) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const oran = Number(satir.gorevsizOran);
  const renk = !Number.isNaN(oran) && oran > 10 ? 'text-amber-800' : 'text-slate-700';

  // Yüzde, bir ondalık, METİN — olduğu gibi basılır.
  return <span className={`whitespace-nowrap font-medium ${renk}`}>%{satir.gorevsizOran}</span>;
}

function GorevsizTablosu({
  satirlar,
  bekliyor,
}: {
  satirlar: GorevsizMesaiSatiri[];
  bekliyor: boolean;
}) {
  return (
    <Tablo
      sutunlar={GOREVSIZ_SUTUNLARI}
      satirlar={satirlar}
      anahtar={(g) => `${g.birimId}:${g.ay}`}
      bekliyor={bekliyor}
      bosMetin="Bu aralıkta kayıt yok."
    />
  );
}
